/** @jsxImportSource @emotion/react */
import { ReactNode, useEffect } from 'react'
import Button from 'react-bootstrap/Button'

import Input from 'components/atoms/Input'
import Select from 'components/atoms/Select'
import { BILLING_LOCATION_CODES } from 'components/lab/constants/payors'
import {
  BILLING_PROVIDER_OPTIONS,
  billedFallback,
  CHARGE_LOCATION_OPTIONS,
  ChargeConfigFormValues,
  configFormProblem,
  ContractedChoice,
  CONTRACTED_OPTIONS,
  CPT_CODE_OPTIONS,
  cptCodesForTestType,
  defaultsToPrimary,
  formatRateAmount,
  HST_UNITS_OPTIONS,
  HstMode,
  MAX_RATE_DAY,
  StoredChargeConfig,
  TEST_TYPE_OPTIONS,
} from 'components/lab/payer/chargeRules/chargeRule'
import { HST_UNITS_NOT_SET } from 'components/lab/payer/chargeRules/chargeConfigTable'
import { HST_CODES_EARLIEST_EFFECTIVE_FROM } from 'components/lab/payer/hstCodes/hstCodes'
import HstTierGrid from 'components/lab/payer/hstCodes/HstTierGrid'

type Props = {
  values: ChargeConfigFormValues
  setValues: (update: (previous: ChargeConfigFormValues) => ChargeConfigFormValues) => void
  /** The payer's whole configuration set: what a save is checked against. */
  configs: StoredChargeConfig[]
  onSave: () => void
  onCancel: () => void
  saving?: boolean
  /** Rendered above the fields; the settings screen puts its payer picker here. */
  children?: ReactNode
  /** Blocks Save while the caller still needs something (e.g. a payer). */
  disabled?: boolean
  /** Extra classes on the panel, e.g. spacing when it opens inside a page. */
  className?: string
  /**
   * The payer's configurations are still loading. Save waits for them instead
   * of judging the row against a partial set (an edited row not found yet
   * reads as a new one).
   */
  loading?: boolean
}

const help = { fontSize: '0.8rem', color: '#6c757d' }

const HST_MODE_OPTIONS: [HstMode, string][] = [
  ['GLOBAL', 'Global — one CPT'],
  ['SPLIT', 'Split — tiered TC and PC codes'],
]

// The CPTs a test type may use, keeping a stored code that is no longer on the list.
const cptOptionsFor = (testType: string, current: string) => {
  const allowed = cptCodesForTestType(testType)
  const options = CPT_CODE_OPTIONS.filter((option) => allowed.includes(option.key))

  return current && !allowed.includes(current)
    ? [...options, { key: current, value: current }]
    : options
}

/**
 * The add/edit modal for one charge configuration, shared by the per-payer
 * Charges tab and the cross-payer Charge Rules screen. Top to bottom: its
 * dates, the test type, the HST mode, then either a Global row's CPT and
 * prices or a Split row's tier prices.
 */
const ChargeRuleForm = ({
  values,
  setValues,
  configs,
  onSave,
  onCancel,
  saving,
  children,
  disabled,
  className,
  loading,
}: Props) => {
  const isNew = !values.id
  const isHst = values.testType === 'HST'
  const split = isHst && values.hstMode === 'SPLIT'
  const stored = values.id ? configs.find((config) => config.id === values.id) : undefined
  const sinceStart = Boolean(stored && stored.effectiveFrom === null)
  const problem = loading ? null : configFormProblem(values, configs)
  // A migrated row may have no billed amount; it bills the next row's.
  const fallback = stored && stored.billedAmount === null ? billedFallback(configs, stored) : null

  const set = (patch: Partial<ChargeConfigFormValues>) => setValues((p) => ({ ...p, ...patch }))

  // A new HST Global row bills two nights unless told otherwise, and says so
  // in its state, so what the select shows is what is saved. That covers New
  // from this on a migrated row stored without units too.
  const needsDefaultUnits = isNew && isHst && !split && (values.hstUnitsBillable ?? null) === null
  useEffect(() => {
    if (needsDefaultUnits) {
      setValues((p) => ({ ...p, hstUnitsBillable: p.hstUnitsBillable ?? 2 }))
    }
  }, [needsDefaultUnits, setValues])

  const changeTestType = (testType: string) =>
    setValues((p) => {
      const cptCode = ['POX', 'COX'].includes(testType) ? '94762' : ''
      const hstMode = testType === 'HST' ? p.hstMode : 'GLOBAL'

      return {
        ...p,
        testType,
        cptCode,
        hstMode,
        hstUnitsBillable:
          testType === 'HST' ? p.hstUnitsBillable ?? (p.id ? undefined : 2) : undefined,
        chargeLocationCode: p.chargeLocationCode || '11',
        isPrimary:
          p.id || !testType ? p.isPrimary : defaultsToPrimary(configs, testType, cptCode, hstMode),
      }
    })

  // Picking the CPT of today's primary on a new row makes it that series' next
  // period, so Primary turns on. It never turns off here: a copy of a primary
  // row keeps its flag when its CPT changes.
  const changeCptCode = (cptCode: string) =>
    setValues((p) => ({
      ...p,
      cptCode,
      isPrimary:
        p.isPrimary ||
        (!p.id &&
          Boolean(p.testType) &&
          defaultsToPrimary(configs, p.testType, cptCode, p.hstMode)),
    }))

  const claimSetupFields = (
    <>
      <div className="col-lg-4 col-md-6 mb-2">
        <Select
          label="Location"
          value={values.chargeLocationCode}
          name="chargeLocationCode"
          wrapperClassName="m-0"
          handleChange={(event) => set({ chargeLocationCode: event.target.value })}
          options={CHARGE_LOCATION_OPTIONS}
        />
      </div>
      <div className="col-lg-4 col-md-6 mb-2">
        <Select
          label="Billing Location Code"
          value={values.billingLocationCode}
          name="billingLocationCode"
          wrapperClassName="m-0"
          handleChange={(event) =>
            set({
              billingLocationCode: event.target.value ? Number(event.target.value) : undefined,
            })
          }
          options={BILLING_LOCATION_CODES}
        />
      </div>
      <div className="col-lg-4 col-md-12 mb-2">
        <Select
          label="Billing Provider"
          value={values.billingProvider}
          name="billingProvider"
          wrapperClassName="m-0"
          handleChange={(event) => set({ billingProvider: event.target.value || undefined })}
          options={BILLING_PROVIDER_OPTIONS}
        />
      </div>
    </>
  )

  const contractedField = (
    <Select
      label="Contracted"
      value={values.contracted}
      name="contracted"
      wrapperClassName="m-0"
      defaultLabel="Payer setting"
      handleChange={(event) => set({ contracted: event.target.value as ContractedChoice })}
      options={CONTRACTED_OPTIONS}
    />
  )

  return (
    <div className={`border rounded p-3 mb-3 ${className ?? ''}`} css={{ background: '#f8fbfd' }}>
      <div className="row">
        <div className="col-12 mb-2">
          <strong>{isNew ? 'Add Charge' : 'Edit Charge'}</strong>
        </div>
        {children}
        <div className="col-lg-4 col-md-6 mb-2">
          <label className="text-gray" htmlFor="effectiveFrom">
            Effective from
          </label>
          <input
            type="date"
            className="form-control"
            id="effectiveFrom"
            name="effectiveFrom"
            // Only a migrated row in force since the start may stay blank.
            required={!sinceStart}
            value={values.effectiveFrom}
            min={split ? HST_CODES_EARLIEST_EFFECTIVE_FROM : undefined}
            max={MAX_RATE_DAY}
            onChange={(event) => set({ effectiveFrom: event.target.value })}
          />
          {sinceStart && !values.effectiveFrom && (
            <div css={help}>Blank: in force since the start.</div>
          )}
        </div>
        <div className="col-lg-4 col-md-6 mb-2">
          <label className="text-gray" htmlFor="endsOn">
            Ends on (stops billing)
          </label>
          <input
            type="date"
            className="form-control"
            id="endsOn"
            name="endsOn"
            value={values.endsOn}
            min={values.effectiveFrom || undefined}
            max={MAX_RATE_DAY}
            onChange={(event) => set({ endsOn: event.target.value })}
          />
          <div css={help}>Leave blank to bill until a newer row starts.</div>
        </div>
      </div>
      <div className="row border-top pt-3 mt-2">
        <div className="col-lg-4 col-md-6 mb-2">
          <Select
            label="Test Type"
            value={values.testType}
            name="testType"
            wrapperClassName="m-0"
            handleChange={(event) => changeTestType(event.target.value)}
            options={TEST_TYPE_OPTIONS}
          />
        </div>
        {isHst && (
          <div className="col-lg-8 col-md-6 mb-2">
            <label className="text-gray d-block">HST billing</label>
            <div className="d-flex flex-wrap" css={{ gap: 16, paddingTop: 6 }}>
              {HST_MODE_OPTIONS.map(([mode, label]) => (
                <label
                  key={mode}
                  className="mb-0 d-flex align-items-center"
                  css={{ userSelect: 'none', cursor: isNew ? 'pointer' : 'default' }}>
                  <input
                    type="radio"
                    className="mr-2"
                    name="hstMode"
                    value={mode}
                    checked={values.hstMode === mode}
                    disabled={!isNew}
                    onChange={() =>
                      setValues((p) => ({
                        ...p,
                        hstMode: mode,
                        isPrimary: mode === 'SPLIT' ? true : p.isPrimary,
                      }))
                    }
                  />
                  {label}
                </label>
              ))}
            </div>
            {!isNew && (
              <div css={help}>
                Fixed once saved. To switch, use New from this and pick the other mode.
              </div>
            )}
          </div>
        )}
      </div>
      {split ? (
        <div className="row">
          {claimSetupFields}
          <div className="col-12 mt-2">
            <HstTierGrid
              prices={values.hstPrices}
              name="hstPrices"
              onPriceChange={(tier, component, field, value) =>
                setValues((p) => ({
                  ...p,
                  hstPrices: {
                    ...p.hstPrices,
                    [tier]: {
                      ...p.hstPrices[tier],
                      [component]: { ...p.hstPrices[tier][component], [field]: value },
                    },
                  },
                }))
              }
            />
          </div>
          <div className="col-lg-4 col-md-6 mt-2">{contractedField}</div>
          <div className="col-lg-8 col-md-6 mt-2 d-flex flex-column justify-content-end">
            <label
              className="mb-1 d-flex align-items-center"
              css={{ userSelect: 'none', cursor: 'pointer' }}>
              <input
                type="checkbox"
                className="mr-2"
                name="hstSkipFailedStudies"
                checked={values.hstSkipFailedStudies}
                onChange={(event) => set({ hstSkipFailedStudies: event.target.checked })}
              />
              Don&rsquo;t bill failed studies
            </label>
            <div css={help}>
              A completed study bills TC + PC; a failed one TC only, or TC + PC once a physician
              read it.
            </div>
          </div>
        </div>
      ) : (
        <div className="row">
          <div className="col-lg-4 col-md-6 mb-2">
            <Select
              label="CPT Code"
              value={values.cptCode}
              name="cptCode"
              wrapperClassName="m-0"
              handleChange={(event) => changeCptCode(event.target.value)}
              options={cptOptionsFor(values.testType, values.cptCode)}
            />
          </div>
          {claimSetupFields}
          {isHst && (
            <div className="col-lg-4 col-md-6 mb-2">
              <Select
                label="HST Units Billable"
                value={values.hstUnitsBillable}
                name="hstUnitsBillable"
                wrapperClassName="m-0"
                // Only a row stored without units offers to stay that way.
                defaultLabel={HST_UNITS_NOT_SET}
                noDefault={!stored || (stored.hstUnitsBillable ?? null) !== null}
                handleChange={(event) =>
                  set({
                    hstUnitsBillable: event.target.value ? Number(event.target.value) : undefined,
                  })
                }
                options={HST_UNITS_OPTIONS}
              />
            </div>
          )}
          <div className="col-lg-4 col-md-6 mb-2">
            <Input
              type="number"
              step="0.01"
              label="Billed"
              name="billedAmount"
              value={values.billedAmount}
              placeholder={stored && stored.billedAmount === null ? 'Not recorded' : 'Billed'}
              handleChange={(event) => set({ billedAmount: event.target.value })}
            />
            {stored && stored.billedAmount === null && values.billedAmount.trim() === '' && (
              <div css={help}>
                Not recorded:{' '}
                {fallback === null
                  ? 'no later row states an amount.'
                  : `bills ${formatRateAmount(fallback)}, the next row's amount.`}
              </div>
            )}
          </div>
          <div className="col-lg-4 col-md-6 mb-2">
            <Input
              type="number"
              step="0.01"
              label="Contract Rate"
              name="contractRate"
              value={values.contractRate}
              placeholder="No rate"
              handleChange={(event) => set({ contractRate: event.target.value })}
            />
          </div>
          <div className="col-lg-4 col-md-6 mb-2">{contractedField}</div>
        </div>
      )}
      <div className="d-flex align-items-center justify-content-end border-top pt-3 mt-3">
        <label
          className="mb-0 mr-auto d-flex align-items-center"
          css={{ userSelect: 'none', cursor: split ? 'default' : 'pointer' }}>
          <input
            type="checkbox"
            className="mr-2"
            checked={split || values.isPrimary}
            disabled={split}
            onChange={(event) => set({ isPrimary: event.target.checked })}
          />
          Primary — used automatically when a charge is created
          {split && <span css={{ ...help, marginLeft: 6 }}>(always on for Split)</span>}
        </label>
        {loading && (
          <span className="mr-3" css={help}>
            Loading the payor&rsquo;s charges&hellip;
          </span>
        )}
        {problem && (
          <span className="mr-3" css={{ fontSize: '0.85rem', maxWidth: 360, color: '#856404' }}>
            {problem}
          </span>
        )}
        <Button variant="outline-secondary" className="mr-2" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          disabled={Boolean(problem) || disabled || saving || loading}
          variant="primary"
          onClick={onSave}>
          {loading ? 'Loading…' : isNew ? 'Add Charge' : 'Save Changes'}
        </Button>
      </div>
    </div>
  )
}

export default ChargeRuleForm
